import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Globe } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@scio3/ui';
import { useLanguage } from '@/hooks/useLanguage';

type Props = {
  /** Which side the menu opens toward. Footer placement opens up ('top');
   *  a header/top-right placement should open down ('bottom'). */
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
};

/**
 * Language picker for the onboarding survey. Same behaviour and language list as
 * the nav `LanguageSwitcher` (shared `useLanguage` hook — persists to profile +
 * localStorage), but surfaced as a standalone dropdown instead of a nested
 * account-menu sub-menu so it can sit on its own (survey footer, or the
 * top-right of the Version B modal).
 */
export function SurveyLanguageMenu({ side = 'top', align = 'center' }: Props = {}) {
  const { t } = useTranslation();
  const { language, setLanguage, languages } = useLanguage();
  const current = languages.find((l) => l.key === language);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="cursor-pointer gap-1.5 text-muted-foreground hover:text-foreground"
          aria-label={t('Language')}
        >
          <Globe className="h-4 w-4" />
          {t('Language: {{language}}', { language: current?.name ?? 'English' })}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="z-[101]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.key}
            onClick={() => setLanguage(lang.key)}
            className="min-w-40 cursor-pointer justify-between gap-4"
          >
            {lang.name}
            {lang.key === language && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
